import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Box, IconButton, Stack } from '@mui/material';
import { ChevronLeft, ChevronRight, InsertDriveFile } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { getAssetUrl } from 'utils/getAssetUrl';

import { equipmentImagesApi, GetEquipmentAssetsResponse, GetEquipmentAssetsVariables } from '../../api';

export interface EquipmentAssetItem {
  id: string;
  source: string;
}

interface Props {
  /** Pass equipmentId to load images from API for an existing equipment (skipped when assets are provided) */
  equipmentId?: string;
  /** Pass already-fetched assets to avoid an extra query */
  assets?: EquipmentAssetItem[];
  /** Pass local File[] for a newly created (not yet saved) equipment */
  localImages?: File[];
}

export const EquipmentImagesCarousel = ({ equipmentId, assets: assetsProp, localImages }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data } = useQuery<GetEquipmentAssetsResponse, GetEquipmentAssetsVariables>(equipmentImagesApi.fetchAssets, {
    variables: { input: { entityId: equipmentId! } },
    skip: !equipmentId || assetsProp !== undefined,
  });

  const remoteUrls = useMemo(() => {
    const assets = assetsProp ?? data?.equipment?.result?.assets ?? [];
    return assets.map(a => getAssetUrl(a.source));
  }, [assetsProp, data]);

  const localUrls = useMemo(() => {
    // Возвращаем пустой массив пока, заполним через useEffect
    return [];
  }, []);

  const [localDataUrls, setLocalDataUrls] = useState<string[]>([]);

  useEffect(() => {
    if (!localImages?.length) {
      setLocalDataUrls([]);
      return;
    }

    let cancelled = false;

    Promise.all(
      localImages.map(
        file =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    ).then(urls => {
      if (!cancelled) setLocalDataUrls(urls);
    });

    return () => {
      cancelled = true;
    };
  }, [localImages]);

  const urls = localImages !== undefined ? localDataUrls : remoteUrls;

  if (urls.length === 0) return null;

  const safeIndex = Math.min(currentIndex, urls.length - 1);
  const currentUrl = urls[safeIndex];

  const handlePrev = () => setCurrentIndex(prev => (prev > 0 ? prev - 1 : urls.length - 1));
  const handleNext = () => setCurrentIndex(prev => (prev < urls.length - 1 ? prev + 1 : 0));

  return (
    <Box sx={{ flex: '0 0 auto', width: '280px' }}>
      {/* Main Image */}
      <Box sx={{ position: 'relative', width: '100%', height: '200px', mb: 1 }}>
        {currentUrl ? (
          <Box
            component="img"
            src={currentUrl}
            alt="Equipment"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: grey[200],
              borderRadius: 1,
            }}
          >
            <InsertDriveFile sx={{ fontSize: 48, color: grey[400] }} />
          </Box>
        )}

        {urls.length > 1 && (
          <>
            <IconButton
              size="small"
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255,255,255,0.8)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255,255,255,0.8)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}
      </Box>

      {/* Thumbnails */}
      {urls.length > 1 && (
        <Stack direction="row" gap={0.5} overflow="auto">
          {urls.map((url, idx) => (
            <Box
              key={idx}
              component="img"
              src={url}
              alt={`thumb-${idx}`}
              onClick={() => setCurrentIndex(idx)}
              sx={{
                width: 60,
                height: 45,
                objectFit: 'cover',
                borderRadius: 0.5,
                cursor: 'pointer',
                border: idx === safeIndex ? '2px solid' : '2px solid transparent',
                borderColor: idx === safeIndex ? 'primary.main' : 'transparent',
                opacity: idx === safeIndex ? 1 : 0.7,
                '&:hover': { opacity: 1 },
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
